import { useEffect, useRef, useCallback } from 'react';

// Grid configuration for mesh deformation
const GRID_W = 20;
const GRID_H = 8;

// Physics constants
const SPRING_K = 0.08;
const DAMPING = 0.92;
const DT = 0.16;

export interface MeshTextProps {
  /** Text to display */
  text: string;
  /** Text color (CSS color string) */
  color?: string;
  /** Font family */
  fontFamily?: string;
  /** Font weight */
  fontWeight?: number;
  /** Font size in pixels */
  fontSize?: number;
  /** Enable chromatic aberration color split effect */
  colorSplit?: boolean;
  /** Colors for chromatic aberration [colorA, colorB] */
  splitColors?: [string, string];
  /** Force multiplier for mouse interaction (0-50, default 18) */
  force?: number;
  /** Additional CSS class */
  className?: string;
}

export function MeshText({
  text,
  color = '#ffffff',
  fontFamily = 'Inter, sans-serif',
  fontWeight = 700,
  fontSize = 120,
  colorSplit = true,
  splitColors = ['#ff40c0', '#40ff80'],
  force = 18,
  className = '',
}: MeshTextProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<number>(0);
  
  // State refs
  const gridRef = useRef<{ x: number; y: number; vx: number; vy: number; ox: number; oy: number }[]>([]);
  const cursorRef = useRef({ x: -1000, y: -1000, px: -1000, py: -1000, inside: false });
  const textCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Initialize grid
  const initGrid = useCallback((width: number, height: number) => {
    const grid: { x: number; y: number; vx: number; vy: number; ox: number; oy: number }[] = [];
    for (let j = 0; j <= GRID_H; j++) {
      for (let i = 0; i <= GRID_W; i++) {
        const x = (i / GRID_W) * width;
        const y = (j / GRID_H) * height;
        grid.push({ x, y, vx: 0, vy: 0, ox: x, oy: y });
      }
    }
    gridRef.current = grid;
  }, []);

  // Render text to offscreen canvas
  const renderTextCanvas = useCallback((width: number, height: number, dpr: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${fontWeight} ${fontSize * dpr}px ${fontFamily}`;
    ctx.fillText(text, width / 2, height / 2);
    
    textCanvasRef.current = canvas;
  }, [text, color, fontFamily, fontWeight, fontSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      const rect = wrapper.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width * dpr));
      height = Math.max(1, Math.round(rect.height * dpr));
      
      canvas.width = width;
      canvas.height = height;
      
      initGrid(width, height);
      renderTextCanvas(width, height, dpr);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(wrapper);
    resize();

    // Mouse handlers
    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * dpr;
      const y = (e.clientY - rect.top) * dpr;
      
      if (!cursorRef.current.inside) {
        cursorRef.current.px = x;
        cursorRef.current.py = y;
        cursorRef.current.inside = true;
      }
      cursorRef.current.x = x;
      cursorRef.current.y = y;
    };

    const onPointerLeave = () => {
      cursorRef.current.inside = false;
      cursorRef.current.x = -1000;
      cursorRef.current.y = -1000;
    };

    wrapper.addEventListener('pointermove', onPointerMove);
    wrapper.addEventListener('pointerleave', onPointerLeave);

    // Animation loop
    const tick = () => {
      const cursor = cursorRef.current;
      const grid = gridRef.current;
      const textCanvas = textCanvasRef.current;
      
      if (!textCanvas || grid.length === 0) {
        animationRef.current = requestAnimationFrame(tick);
        return;
      }

      // Calculate cursor velocity
      const cvx = cursor.x - cursor.px;
      const cvy = cursor.y - cursor.py;
      cursor.px = cursor.x;
      cursor.py = cursor.y;

      // Update grid physics
      const forceMultiplier = force / 10;
      for (let i = 0; i < grid.length; i++) {
        const p = grid[i];
        
        // Distance from cursor
        const dx = cursor.x - p.x;
        const dy = cursor.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const radius = Math.max(width, height) * 0.15;
        
        if (dist < radius && cursor.inside) {
          const influence = (1 - dist / radius) * forceMultiplier;
          p.vx += cvx * influence * 0.5;
          p.vy += cvy * influence * 0.5;
        }
        
        // Spring back to original position
        p.vx += (p.ox - p.x) * SPRING_K;
        p.vy += (p.oy - p.y) * SPRING_K;
        
        // Damping
        p.vx *= DAMPING;
        p.vy *= DAMPING;
        
        // Update position
        p.x += p.vx * DT;
        p.y += p.vy * DT;
      }

      // Clear and render
      ctx.clearRect(0, 0, width, height);

      // Draw distorted mesh
      for (let j = 0; j < GRID_H; j++) {
        for (let i = 0; i < GRID_W; i++) {
          const idx = j * (GRID_W + 1) + i;
          const p0 = grid[idx];
          const p1 = grid[idx + 1];
          const p2 = grid[idx + GRID_W + 1];
          const p3 = grid[idx + GRID_W + 2];

          // Source coordinates (from text canvas)
          const sx0 = (i / GRID_W) * width;
          const sy0 = (j / GRID_H) * height;
          const sx1 = ((i + 1) / GRID_W) * width;
          const sy1 = ((j + 1) / GRID_H) * height;
          const sw = sx1 - sx0;
          const sh = sy1 - sy0;

          // Calculate distortion magnitude for chromatic effect
          const distortion = Math.sqrt(
            Math.pow(p0.x - p0.ox, 2) + Math.pow(p0.y - p0.oy, 2) +
            Math.pow(p1.x - p1.ox, 2) + Math.pow(p1.y - p1.oy, 2)
          ) / 2;

          // Draw with chromatic aberration if enabled
          if (colorSplit && distortion > 0.5) {
            const offset = Math.min(distortion * 0.1, 3);
            
            // Red/Pink channel (offset left)
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            ctx.globalAlpha = 0.6;
            drawQuad(ctx, textCanvas, 
              sx0, sy0, sw, sh,
              p0.x - offset, p0.y, p1.x - offset, p1.y, 
              p2.x - offset, p2.y, p3.x - offset, p3.y,
              splitColors[0]
            );
            
            // Cyan/Green channel (offset right)
            drawQuad(ctx, textCanvas,
              sx0, sy0, sw, sh,
              p0.x + offset, p0.y, p1.x + offset, p1.y,
              p2.x + offset, p2.y, p3.x + offset, p3.y,
              splitColors[1]
            );
            ctx.restore();
          }

          // Main text
          ctx.save();
          ctx.globalAlpha = 1;
          drawQuad(ctx, textCanvas,
            sx0, sy0, sw, sh,
            p0.x, p0.y, p1.x, p1.y, p2.x, p2.y, p3.x, p3.y
          );
          ctx.restore();
        }
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
      wrapper.removeEventListener('pointermove', onPointerMove);
      wrapper.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [text, color, fontFamily, fontWeight, fontSize, colorSplit, splitColors, force, initGrid, renderTextCanvas]);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}

// Draw a textured quad using canvas transforms
function drawQuad(
  ctx: CanvasRenderingContext2D,
  tex: HTMLCanvasElement,
  sx: number, sy: number, sw: number, sh: number,
  x0: number, y0: number,
  x1: number, y1: number,
  x2: number, y2: number,
  x3: number, y3: number,
  tintColor?: string
) {
  // Use simple drawImage for the quad (approximation)
  // For better quality, we'd need WebGL, but this works for the effect
  ctx.save();
  
  // Calculate center and average dimensions
  const cx = (x0 + x1 + x2 + x3) / 4;
  const cy = (y0 + y1 + y2 + y3) / 4;
  const avgW = (Math.abs(x1 - x0) + Math.abs(x3 - x2)) / 2;
  const avgH = (Math.abs(y2 - y0) + Math.abs(y3 - y1)) / 2;
  
  // Calculate rotation from distortion
  const angle = Math.atan2(y1 - y0, x1 - x0);
  
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  
  if (tintColor) {
    // For chromatic channels, draw with color overlay
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(tex, sx, sy, sw, sh, -avgW/2, -avgH/2, avgW, avgH);
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = tintColor;
    ctx.fillRect(-avgW/2, -avgH/2, avgW, avgH);
  } else {
    ctx.drawImage(tex, sx, sy, sw, sh, -avgW/2, -avgH/2, avgW, avgH);
  }
  
  ctx.restore();
}
